import React, { useState, useEffect, useRef, useCallback, useMemo } from "react";
import HanziWriter from "hanzi-writer";
import confetti from "canvas-confetti";
import HomeIcon from '@mui/icons-material/Home';
import RefreshIcon from '@mui/icons-material/Refresh';
import TipsAndUpdatesIcon from '@mui/icons-material/TipsAndUpdates';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';

export default function ChineseWritingView({ words, onExit, mode = "spelling" }) {
    const [currentIndex, setCurrentIndex] = useState(0);
    const [score, setScore] = useState(0);
    const [isComplete, setIsComplete] = useState(false);
    const [mistakes, setMistakes] = useState(0);
    const [showSummary, setShowSummary] = useState(false);
    const [results, setResults] = useState([]);
    const [isDrawing, setIsDrawing] = useState(true); // Track if user is still drawing
    const [strokesDrawn, setStrokesDrawn] = useState(0); // Track strokes drawn
    const [totalStrokes, setTotalStrokes] = useState(0); // Total strokes needed

    const writerRef = useRef(null);
    const containerRef = useRef(null);
    const mistakesRef = useRef(0); // Use ref to track mistakes during drawing

    const currentWord = words[currentIndex];
    const currentCharacters = useMemo(() => currentWord?.chinese?.split('') || [], [currentWord]);
    const [currentCharIndex, setCurrentCharIndex] = useState(0);

    // Mode determines behavior:
    // "spelling" = strict quiz mode, counts mistakes, scoring
    // "practice" = relaxed mode, shows hints automatically, no scoring pressure
    const isPracticeMode = mode === "practice";

    // Initialize voices (important for iPad/iOS)
    useEffect(() => {
        const initVoices = () => {
            window.speechSynthesis.getVoices();
        };
        initVoices();
        window.speechSynthesis.onvoiceschanged = initVoices;
        return () => {
            window.speechSynthesis.onvoiceschanged = null;
        };
    }, []);

    const initWriter = useCallback(() => {
        if (!containerRef.current || !currentCharacters[currentCharIndex]) return;

        // Clear previous writer
        if (writerRef.current) {
            writerRef.current = null;
        }
        containerRef.current.innerHTML = '';

        const char = currentCharacters[currentCharIndex];
        mistakesRef.current = 0;
        setIsDrawing(true);
        setStrokesDrawn(0);

        try {
            writerRef.current = HanziWriter.create(containerRef.current, char, {
                width: 250,
                height: 250,
                padding: 10,
                showOutline: isPracticeMode, // Only show outline in practice mode
                showCharacter: isPracticeMode, // Only show character in practice mode
                strokeAnimationSpeed: 1,
                delayBetweenStrokes: 200,
                strokeColor: '#4f46e5',
                outlineColor: '#c7d2fe',
                highlightColor: '#a5b4fc',
                radicalColor: '#6366f1',
                highlightOnComplete: true,
                showHintAfterMisses: isPracticeMode ? 2 : false, // Only auto-hint in practice mode
                highlightCompleteColor: '#22c55e',
                drawingWidth: 20,
                acceptBackwardsStrokes: true,
            });

            // Get stroke count for this character
            const charData = HanziWriter.loadCharacterData(char).then(data => {
                setTotalStrokes(data.strokes.length);
            }).catch(() => {
                setTotalStrokes(0);
            });

            // Start quiz mode but suppress feedback
            writerRef.current.quiz({
                onCorrectStroke: (strokeData) => {
                    setStrokesDrawn(s => s + 1);
                },
                onMistake: (strokeData) => {
                    mistakesRef.current += 1; // Track silently
                },
                onComplete: (summaryData) => {
                    // Now show the results
                    setMistakes(mistakesRef.current);
                    setIsDrawing(false);
                    setIsComplete(true);

                    // Confetti for spelling mode when character completed with few mistakes
                    if (!isPracticeMode && mistakesRef.current <= 2) {
                        confetti({
                            particleCount: 50,
                            spread: 60,
                            origin: { y: 0.7 }
                        });
                    }

                    // Move to next character or word
                    if (currentCharIndex < currentCharacters.length - 1) {
                        setTimeout(() => {
                            setCurrentCharIndex(currentCharIndex + 1);
                            setIsComplete(false);
                            mistakesRef.current = 0;
                        }, 1500);
                    } else {
                        // Word complete - calculate score
                        const wordMistakes = mistakesRef.current;
                        const wordScore = Math.max(0, 10 - wordMistakes);
                        setScore(s => s + wordScore);
                        setResults(r => [...r, {
                            word: currentWord,
                            mistakes: wordMistakes,
                            score: wordScore
                        }]);

                        // Big confetti for completing the word in spelling mode
                        if (!isPracticeMode && wordScore >= 8) {
                            confetti({
                                particleCount: 150,
                                spread: 100,
                                origin: { y: 0.6 }
                            });
                        }
                    }
                }
            });
        } catch (error) {
            console.error('Error creating HanziWriter:', error);
        }
    }, [currentCharIndex, currentCharacters, currentWord, isPracticeMode]);

    useEffect(() => {
        initWriter();
        return () => {
            if (writerRef.current) {
                writerRef.current = null;
            }
        };
    }, [initWriter]);

    useEffect(() => {
        // Reset for new word
        setCurrentCharIndex(0);
        setIsComplete(false);
        setMistakes(0);
        setIsDrawing(true);
        setStrokesDrawn(0);
        mistakesRef.current = 0;
    }, [currentIndex]);

    const handleShowAnimation = () => {
        if (writerRef.current) {
            if (!isPracticeMode) {
                mistakesRef.current += 2; // Penalty for using hint (only in spelling mode)
            }
            writerRef.current.showCharacter();
            writerRef.current.animateCharacter({
                onComplete: () => {
                    writerRef.current.hideCharacter();
                    writerRef.current.quiz({
                        onCorrectStroke: () => {
                            setStrokesDrawn(s => s + 1);
                        },
                        onMistake: () => {
                            mistakesRef.current += 1;
                        },
                        onComplete: () => {
                            setMistakes(mistakesRef.current);
                            setIsDrawing(false);
                            setIsComplete(true);

                            // Confetti for spelling mode when character completed with few mistakes
                            if (!isPracticeMode && mistakesRef.current <= 2) {
                                confetti({
                                    particleCount: 50,
                                    spread: 60,
                                    origin: { y: 0.7 }
                                });
                            }

                            if (currentCharIndex < currentCharacters.length - 1) {
                                setTimeout(() => {
                                    setCurrentCharIndex(currentCharIndex + 1);
                                    setIsComplete(false);
                                    mistakesRef.current = 0;
                                }, 1500);
                            } else {
                                // Word complete
                                const wordMistakes = mistakesRef.current;
                                const wordScore = Math.max(0, 10 - wordMistakes);
                                setScore(s => s + wordScore);
                                setResults(r => [...r, {
                                    word: currentWord,
                                    mistakes: wordMistakes,
                                    score: wordScore
                                }]);

                                // Big confetti for completing the word in spelling mode
                                if (!isPracticeMode && wordScore >= 8) {
                                    confetti({
                                        particleCount: 150,
                                        spread: 100,
                                        origin: { y: 0.6 }
                                    });
                                }
                            }
                        }
                    });
                }
            });
        }
    };

    const handleReset = () => {
        initWriter();
    };

    const handleNext = () => {
        if (currentIndex + 1 < words.length) {
            setCurrentIndex(currentIndex + 1);
        } else {
            setShowSummary(true);
        }
    };

    const speakWord = () => {
        if (!currentWord) return;
        const chinese = currentWord.chinese;
        if (window.speechSynthesis.speaking) {
            window.speechSynthesis.cancel();
        }
        setTimeout(() => {
            const utterance = new window.SpeechSynthesisUtterance(chinese);
            utterance.lang = "zh-CN";
            utterance.rate = 0.8;
            utterance.pitch = 1.0;
            utterance.volume = 1.0;
            
            // Try to select a Chinese voice (helps with iPad/iOS)
            const voices = window.speechSynthesis.getVoices();
            const chineseVoices = voices.filter(v => v.lang.includes('zh') || v.lang.includes('ZH'));
            if (chineseVoices.length > 0) {
                utterance.voice = chineseVoices[0];
            }
            
            utterance.onerror = (event) => {
                console.error('Speech synthesis error:', event);
            };
            window.speechSynthesis.speak(utterance);
        }, 100);
    };

    const isWordComplete = isComplete && currentCharIndex === currentCharacters.length - 1;

    if (showSummary) {
        return (
            <div className="chinese-view">
                <div className="chinese-summary">
                    <h2 className="chinese-summary-title">
                        <CheckCircleIcon className="icon-md" />
                        {isPracticeMode ? 'Practice Complete!' : 'Spelling Complete!'}
                    </h2>
                    {!isPracticeMode && (
                        <div className="chinese-summary-score">
                            Total Score: {score} / {words.length * 10}
                        </div>
                    )}
                    <div className="chinese-summary-list">
                        {results.map((result, index) => (
                            <div key={index} className="chinese-summary-item">
                                <span className="chinese-summary-char">{result.word.chinese}</span>
                                <span className="chinese-summary-pinyin">{result.word.pinyin}</span>
                                {!isPracticeMode && (
                                    <span className={`chinese-summary-result ${result.mistakes === 0 ? 'perfect' : ''}`}>
                                        {result.score}/10
                                    </span>
                                )}
                                {isPracticeMode && (
                                    <span className="chinese-summary-result perfect">
                                        <CheckCircleIcon className="icon-sm" />
                                    </span>
                                )}
                            </div>
                        ))}
                    </div>
                    <button className="btn btn-primary btn-block" onClick={onExit}>
                        <HomeIcon className="icon-sm" />
                        Back to Menu
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="chinese-view">
            <div className="chinese-header">
                <span className="chinese-progress">
                    Character {currentIndex + 1} of {words.length}
                </span>
                <button className="btn btn-link" onClick={onExit}>
                    <HomeIcon className="icon-sm" />
                    Exit
                </button>
            </div>

            <div className="chinese-content">
                {/* Word Info */}
                <div className="chinese-word-info">
                    <div className="chinese-english">{currentWord?.english}</div>
                    <div className="chinese-pinyin">{currentWord?.pinyin}</div>
                    {currentCharacters.length > 1 && (
                        <div className="chinese-char-progress">
                            {currentCharacters.map((char, idx) => (
                                <span
                                    key={idx}
                                    className={`chinese-char-dot ${idx < currentCharIndex ? 'done' : ''} ${idx === currentCharIndex ? 'current' : ''}`}
                                >
                                    {isPracticeMode ? (idx < currentCharIndex ? char : '○') : (idx < currentCharIndex ? '✓' : '○')}
                                </span>
                            ))}
                        </div>
                    )}
                </div>

                {/* Drawing Canvas */}
                <div className="chinese-canvas-wrapper">
                    <div
                        ref={containerRef}
                        className="chinese-canvas"
                    />
                    {isComplete && (
                        <div className="chinese-complete-overlay">
                            <CheckCircleIcon className="icon-xl" />
                        </div>
                    )}
                </div>

                {/* Character Info */}
                <div className="chinese-char-info">
                    {isPracticeMode ? (
                        <>Writing: <strong>{currentCharacters[currentCharIndex]}</strong></>
                    ) : (
                        <>Character {currentCharIndex + 1} of {currentCharacters.length}</>
                    )}
                    {isDrawing ? (
                        <span className="chinese-stroke-progress">
                            {totalStrokes > 0 ? ` (Stroke ${strokesDrawn + 1}/${totalStrokes})` : ' (Drawing...)'}
                        </span>
                    ) : (
                        mistakes > 0 && <span className="chinese-mistakes"> ({mistakes} mistakes)</span>
                    )}
                </div>

                {/* Action Buttons */}
                <div className="chinese-actions">
                    <button className="btn btn-info" onClick={speakWord}>
                        <VolumeUpIcon className="icon-sm" />
                        Listen
                    </button>
                    {isPracticeMode && (
                        <button className="btn btn-warning" onClick={handleShowAnimation}>
                            <TipsAndUpdatesIcon className="icon-sm" />
                            Show Hint
                        </button>
                    )}
                    <button className="btn btn-outline" onClick={handleReset}>
                        <RefreshIcon className="icon-sm" />
                        Reset
                    </button>
                </div>

                {/* Next Button */}
                {isWordComplete && (
                    <button className="btn btn-success btn-lg btn-block" onClick={handleNext}>
                        <ArrowForwardIcon className="icon-md" />
                        {currentIndex + 1 < words.length ? 'Next Word' : 'Finish'}
                    </button>
                )}

                {/* Score - only show in spelling mode */}
                {!isPracticeMode && (
                    <div className="chinese-score">
                        Score: {score}
                    </div>
                )}
            </div>
        </div>
    );
}
